import { useQuery } from "@tanstack/react-query"
import { getStoryGeneration } from "@/lib/api/admin-stories"
import type { StoryGeneration } from "@/lib/api/admin-stories"
import { CollapsibleSection } from "@/components/admin/collapsible-section"

export function StoryGenerationPanel({ id }: { id: string }) {
  const { data: gen } = useQuery({
    queryKey: ["story", id, "generation"],
    queryFn: () => getStoryGeneration(id),
  })

  if (!gen) return null

  const byType = (
    gen.knowledgeItems as StoryGeneration["knowledgeItems"]
  ).reduce<Record<string, string[]>>((acc, item) => {
    ;(acc[item.type] ??= []).push(item.text)
    return acc
  }, {})

  return (
    <div className="mt-8 space-y-3 rounded-2xl border border-dashed p-4 text-sm">
      <p className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
        Generation
      </p>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Model</span>
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            {gen.model}
          </code>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(gen.createdAt).toLocaleString()}
        </span>
        {(gen.storyCostUsd !== null || gen.quizCostUsd !== null) && (
          <span className="text-xs text-muted-foreground">
            {gen.storyCostUsd !== null && (
              <>story: ${gen.storyCostUsd.toFixed(4)}</>
            )}
            {gen.storyCostUsd !== null && gen.quizCostUsd !== null && ", "}
            {gen.quizCostUsd !== null && (
              <>quiz: ${gen.quizCostUsd.toFixed(4)}</>
            )}
          </span>
        )}
      </div>

      {gen.customInstruction && (
        <div>
          <p className="mb-1 text-xs text-muted-foreground">
            Custom instruction
          </p>
          <blockquote className="rounded-lg border-l-2 border-muted-foreground/30 bg-muted px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap">
            {gen.customInstruction}
          </blockquote>
        </div>
      )}

      <CollapsibleSection label="Prompt">
        <pre className="max-h-64 overflow-auto rounded-lg bg-muted p-3 text-xs leading-relaxed whitespace-pre-wrap">
          {gen.prompt}
        </pre>
      </CollapsibleSection>

      <CollapsibleSection
        label={`Knowledge items — ${["word", "kanji", "rule"].map((t) => `${byType[t]?.length ?? 0} ${t === "word" ? "vocab" : t === "rule" ? "rules" : "kanji"}`).join(", ")}`}
      >
        <div className="space-y-2">
          {Object.entries(byType).map(([type, items]) => (
            <div key={type}>
              <p className="mb-1 text-xs font-medium text-muted-foreground capitalize">
                {type}
              </p>
              <p className="text-xs leading-relaxed">{items.join("、 ")}</p>
            </div>
          ))}
        </div>
      </CollapsibleSection>
    </div>
  )
}
